import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostListener,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs";

import { SnackbarService } from "@intocare/ui";

@Component({
	selector: "pv-login-form",
	templateUrl: "./login-form.component.html",
	styleUrls: ["./login-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LoginFormComponent implements OnInit, OnDestroy {
	private formBuilder = inject(UntypedFormBuilder);
	private readonly snackbar = inject(SnackbarService);
	private route = inject(ActivatedRoute);

	@Input() loading: boolean;
	@Output() authenticate = new EventEmitter();

	form: UntypedFormGroup;
	hasOrganisation = false;
	userActivity: ReturnType<typeof setTimeout>;
	userInactive = new Subject<boolean>();
	userInactiveSubscription: Subscription;

	constructor() {
		this.form = this.formBuilder.group({
			username: ["", Validators.required],
			password: ["", Validators.required],
			organisation: ["", Validators.required],
		});

		this.userInactiveSubscription = this.userInactive.subscribe((next: boolean) => {
			if (next) {
				console.log("user has been inactive for 2 minutes");
				this.form.reset({
					organisation: this.form.get("organisation").value,
				});
			}
		});

		this.route.queryParams.subscribe((queryParams) => {
			if (queryParams["redirectedByInactivity"]) {
				this.snackbar.show({ text: "labels.login.warning.redirectedByInactivity", warning: true });
			}
		});
	}

	@Input() landing = false;

	@Input()
	set organisation(value: string) {
		if (!value) {
			return;
		}

		this.form.patchValue({ organisation: value });
		this.hasOrganisation = true;
	}

	get invalidUsername() {
		const control = this.form.get("username");

		return control.touched && control.hasError("required");
	}

	get invalidPassword() {
		const control = this.form.get("password");

		return control.touched && control.hasError("required");
	}

	get invalidOrganisation() {
		const control = this.form.get("organisation");

		return control.touched && control.hasError("required");
	}

	ngOnDestroy(): void {
		this.clearInactivityTimeout();
		this.userInactiveSubscription.unsubscribe();
	}

	clearInactivityTimeout() {
		if (this.userActivity) {
			// clear any existing timeout
			clearTimeout(this.userActivity);
		}
	}

	setInactivityTimeout() {
		this.clearInactivityTimeout();
		// start the timer over again 2 minutes
		this.userActivity = setTimeout(() => this.userInactive.next(true), 2 * 60 * 1000);
	}

	submit() {
		for (const key of Object.keys(this.form.controls)) {
			this.form.controls[key].markAsTouched();
		}

		if (!this.form.valid) {
			return;
		}

		const formValue = this.form.value;

		this.authenticate.emit({
			...formValue,
			organisation: formValue.organisation.toLowerCase(),
		});
	}

	clearPassword() {
		this.form.get("password").reset();
	}

	ngOnInit() {
		this.form.reset({
			username: "",
			password: "",
			organisation: this.form.get("organisation").value,
		});
	}

	@HostListener("window:mousemove") refreshUserState() {
		this.setInactivityTimeout();
	}

	@HostListener("window:keydown") refreshUserState2() {
		this.setInactivityTimeout();
	}

	@HostListener("window:scroll") refreshUserState3() {
		this.setInactivityTimeout();
	}

	@HostListener("window:click") refreshUserState4() {
		this.setInactivityTimeout();
	}
}
